import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { REACT_APP_API_URL } from '../config';
import { apiFetch } from '../App';
import { getUserEmail } from '../utils/userUtils';
import './Compare.css';

function Compare() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [stats, setStats] = useState({ completed: 0, total: 0 });
  const [imageLoadingStates, setImageLoadingStates] = useState({
    image_1: true,
    image_2: true
  });
  const [experiment, setExperiment] = useState(null);
  const [experiments, setExperiments] = useState([]);
  const [selectedExperimentId, setSelectedExperimentId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  const navigate = useNavigate();
  const { experimentId } = useParams();
  const location = useLocation();

  console.log('Compare component rendered. experimentId from params:', experimentId);

  // Reset state when component mounts or experimentId changes
  useEffect(() => {
    // Only reset if the experimentId changes to a new value
    if (experimentId !== selectedExperimentId) {
      console.log('Resetting component state due to experimentId change:', experimentId);
      setComparison(null);
      setExperiment(null);
      setLoading(true);
      setError(null);
    }
  }, [experimentId, selectedExperimentId]);

  // Main data loading effect
  useEffect(() => {
    // Fetch all experiments first
    console.log('Initial useEffect - fetching experiments');
    fetchExperiments();
    
    // If we have an experiment ID from URL params, use that
    if (experimentId) {
      console.log('Found experimentId in URL, fetching experiment:', experimentId);
      fetchExperiment(experimentId);
    } else {
      // If no experimentId, just stop loading
      setLoading(false);
    }
  }, [experimentId, location.pathname]);

  // Update selected experiment state when experimentId changes
  useEffect(() => {
    if (experimentId && experimentId !== selectedExperimentId) {
      console.log('Setting selected experiment to match URL parameter:', experimentId);
      setSelectedExperimentId(experimentId);
    }
  }, [experimentId, selectedExperimentId]);

  // When selected experiment changes, fetch its data
  useEffect(() => {
    if (selectedExperimentId && !experimentId) {
      console.log('Selected experiment changed to:', selectedExperimentId);
      // Update URL to reflect the selected experiment
      navigate(`/compare/${selectedExperimentId}`, { replace: true });
    }
  }, [selectedExperimentId, experimentId, navigate]);

  // When experiment changes, fetch the next comparison
  useEffect(() => {
    if (experiment && experimentId) {
      // Using experiment data
      fetchNextComparisonForExperiment();
    }
  }, [experiment]);

  // Add effect to clean up object URLs when component unmounts or comparison changes
  useEffect(() => {
    // Cleanup function to revoke object URLs
    return () => {
      if (comparison) {
        if (comparison.image1_url && comparison.image1_url.startsWith('blob:')) {
          URL.revokeObjectURL(comparison.image1_url);
          console.log('Revoked object URL for image 1');
        }
        if (comparison.image2_url && comparison.image2_url.startsWith('blob:')) {
          URL.revokeObjectURL(comparison.image2_url);
          console.log('Revoked object URL for image 2');
        }
      }
    };
  }, [comparison]);

  const fetchExperiments = async () => {
    try {
      console.log('Fetching all experiments from:', 'experiments');
      
      const response = await apiFetch('experiments');
      
      console.log('Experiments response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        
        if (Array.isArray(data) && data.length === 0) {
          console.log('No experiments found - showing empty state');
          setExperiments([]);
        } else if (data) {
          console.log('Experiments data received:', data);
          // Filter to only show active experiments
          const activeExperiments = data.filter(exp => exp.status === 'active');
          setExperiments(activeExperiments);
        } else {
          setError('Failed to parse experiments data');
        }
      } else {
        const errorText = await response.text();
        console.error('Failed to load experiments:', errorText);
        setError(`Failed to load experiments: ${response.status} ${errorText}`);
      }
    } catch (error) {
      console.error('Error fetching experiments:', error);
      if (error.name === 'AbortError') {
        setError('Request timed out. The server may be unavailable.');
      } else {
        setError(`Error: ${error.message}`);
      }
    }
  };

  const fetchExperiment = async (id) => {
    try {
      setLoading(true);
      
      let userEmail;
      try {
        userEmail = await getUserEmail();
      } catch (authError) {
        console.error('Auth error:', authError);
        setError('Authentication required. Please log in again.');
        navigate('/');
        return;
      }
      
      const response = await apiFetch('experiments/' + id + '?user_id=' + encodeURIComponent(userEmail));
      
      if (response.ok) {
        const experimentData = await response.json();
        setExperiment(experimentData);
        
        // Update stats from experiment data
        setStats({
          completed: experimentData.completed,
          total: experimentData.total
        });
      } else {
        const errorText = await response.text();
        setError(`Failed to load experiment: ${errorText}`);
      }
    } catch (error) {
      setError(`Error: ${error.message}`);
      setLoading(false);
    }
  };

  const fetchNextComparisonForExperiment = async () => {
    try {
      // Don't set loading true for the whole page, just for the images
      setError(null);
      setImageLoadingStates({ image_1: true, image_2: true });
      
      let userEmail;
      try {
        userEmail = await getUserEmail();
      } catch (authError) {
        console.error('Auth error:', authError);
        setError('Authentication required. Please log in again.');
        navigate('/');
        return;
      }
      
      const url = REACT_APP_API_URL + '/experiments/' + experimentId + '/next-comparison?user_id=' + encodeURIComponent(userEmail);
      console.log('Fetching next comparison from URL:', url);
      
      const response = await apiFetch(url);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch comparison: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Comparison data:', data);
      
      handleComparisonData(data);
    } catch (error) {
      setError(`Failed to fetch next comparison: ${error.message}`);
      console.error('Error fetching comparison:', error);
      setLoading(false);
    }
  };

  const handleComparisonData = (data) => {
    if (data.status === 'no_images' || data.status === 'complete') {
      setError(data.status === 'no_images' 
        ? 'No more images to compare between these models.' 
        : 'All comparisons completed!');
      setComparison(null);
      setLoading(false);
      
      // If all comparisons for an experiment are complete, navigate to results
      if (experimentId && data.status === 'complete') {
        setTimeout(() => {
          navigate(`/results/${experimentId}`);
        }, 3000);
      }
      
      return;
    }

    // Get the order of models from the API
    const modelOrder = data.order || [experiment.model_1, experiment.model_2];
    console.log('Model display order:', modelOrder);

    // Set up the comparison with the model order from the API
    setComparison({
      image_name: data.image_name,
      // Store models in the order specified by the API
      model_1: modelOrder[0],
      model_2: modelOrder[1],
      // Store experiment ID if provided
      experiment_id: data.experiment_id || experimentId,
    });

    // Start preloading images
    loadImages(data.image_name, modelOrder);
  };

  const loadImages = async (imageName, modelOrder) => {
    // Set loading state for the images only
    setImageLoadingStates({ image_1: true, image_2: true });
    
    try {
      // Create image URLs
      const image1Path = 'images/' + modelOrder[0] + '/' + imageName;
      const image2Path = 'images/' + modelOrder[1] + '/' + imageName;
      
      console.log(`Image paths: ${image1Path} and ${image2Path}`);
      
      // Use apiFetch to get the images with proper authentication
      const fetchImage = async (url) => {
        const response = await apiFetch(url);
        if (!response.ok) {
          throw new Error(`Failed to load image: ${response.status}`);
        }
        const blob = await response.blob();
        return URL.createObjectURL(blob);
      };
      
      // Fetch both images in parallel
      const [image1Url, image2Url] = await Promise.all([
        fetchImage(image1Path),
        fetchImage(image2Path)
      ]);
      
      // Create load promises for both images to ensure they're loaded in the DOM
      const loadImage1 = new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          resolve({ success: true, url: image1Url });
        };
        img.onerror = (error) => {
          console.error('Error loading Image A:', error);
          resolve({ success: false });
        };
        img.src = image1Url;
      });
      
      const loadImage2 = new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          resolve({ success: true, url: image2Url });
        };
        img.onerror = (error) => {
          console.error('Error loading Image B:', error);
          resolve({ success: false });
        };
        img.src = image2Url;
      });
      
      // Wait for both images to load in the DOM
      const [image1Result, image2Result] = await Promise.all([loadImage1, loadImage2]);
      
      // Only update the comparison object once both images are loaded
      setComparison(prev => ({
        ...prev,
        image1_url: image1Result.success ? image1Result.url : null,
        image2_url: image2Result.success ? image2Result.url : null
      }));
      
      // Update loading states based on success
      setImageLoadingStates({
        image_1: !image1Result.success,
        image_2: !image2Result.success
      });
      
      // Set loading to false after everything is done
      setLoading(false);
    } catch (error) {
      console.error('Error fetching images:', error);
      setError('Failed to load images. Please try again.');
      setLoading(false);
    }
  };

  const submitComparison = async (choice) => {
    try {
      // Set submitting state to true instead of loading the whole page
      setSubmitting(true);
      setError(null);
      
      if (!comparison) return;

      let userEmail;
      try {
        userEmail = await getUserEmail();
      } catch (authError) {
        console.error('Auth error:', authError);
        setError('Authentication required. Please log in again.');
        navigate('/');
        return;
      }

      // Map the choice to the result format matching Python implementation
      let result;
      if (choice === "Image A is better") {
        result = comparison.model_1;  // winning model name
      } else if (choice === "Image B is better") {
        result = comparison.model_2;  // winning model name
      } else if (choice === "Equally good") {
        result = "equally_good";
      } else {  // "Equally bad"
        result = "equally_bad";
      }

      const comparison_data = {
        user: userEmail,
        model_1: comparison.model_1,
        model_2: comparison.model_2,
        image_name: comparison.image_name,
        result: result,
        experiment_id: comparison.experiment_id
      };

      console.log('Submitting comparison:', comparison_data);
      
      const response = await apiFetch('comparisons', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(comparison_data)
      });

      if (!response.ok) {
        throw new Error(`Failed to submit comparison: ${response.status}`);
      }

      // If we're in an experiment, increment the completed count
      if (experiment) {
        setStats(prev => ({
          ...prev,
          completed: prev.completed + 1
        }));
        
        // Also update the experiment object
        setExperiment(prev => ({
          ...prev,
          completed: prev.completed + 1
        }));
      }

      // Fetch next comparison
      fetchNextComparisonForExperiment();
    } catch (error) {
      setError(`Failed to submit comparison: ${error.message}`);
      console.error('Error submitting comparison:', error);
    } finally {
      // Always set submitting to false when done
      setSubmitting(false);
    }
  };

  const handleVote = (choice) => {
    let translatedChoice;
    
    switch(choice) {
      case 'A':
        translatedChoice = "Image A is better";
        break;
      case 'B':
        translatedChoice = "Image B is better";
        break;
      case 'EquallyGood':
        translatedChoice = "Equally good";
        break;
      case 'EquallyBad':
        translatedChoice = "Equally bad";
        break;
      default:
        translatedChoice = "Equally good";
    }
    
    submitComparison(translatedChoice);
  };

  const renderImage = (imageUrl, imageIndex) => {
    const loadingKey = `image_${imageIndex}`;
    const label = imageIndex === 1 ? 'A' : 'B';
    
    return (
      <div className="image-wrapper">
        <div className="image-label">Image {label}</div>
        
        {/* Always try to render the image */}
        {imageUrl && (
          <img
            src={imageUrl}
            alt={`Image ${label}`}
            className="comparison-image"
            style={{ opacity: imageLoadingStates[loadingKey] ? 0.3 : 1 }}
          />
        )}
        
        {/* Show loading spinner overlay while loading */}
        {imageLoadingStates[loadingKey] && (
          <div className="image-loading">
            <div className="loading-spinner"></div>
            <span>Loading image...</span>
          </div>
        )}
        
        {/* Show error message if image failed to load */}
        {!imageUrl && !imageLoadingStates[loadingKey] && (
          <div className="image-error">
            <p>Failed to load image</p>
          </div>
        )}
      </div>
    );
  };

  const renderExperimentSelection = () => {
    return (
      <div className="experiment-selector-compact">
        <select 
          value={selectedExperimentId || ''} 
          onChange={(e) => {
            const newValue = e.target.value;
            console.log('Experiment selected:', newValue);
            if (newValue) {
              setSelectedExperimentId(newValue);
              navigate(`/compare/${newValue}`);
            }
          }}
        >
          <option value="">-- Select an Experiment --</option>
          {experiments.map(exp => (
            <option key={exp.id} value={exp.id}>
              {exp.name} - {exp.model_1} vs {exp.model_2}
            </option>
          ))}
        </select>
      </div>
    );
  };

  const renderExperimentProgress = () => {
    if (!experiment) return null;
    
    return (
      <div className="experiment-progress-compact">
        <div className="experiment-meta">
          <h3>{experiment.name}</h3>
          <div className="model-info-compact">
            <span>{experiment.model_1}</span> vs <span>{experiment.model_2}</span>
          </div>
        </div>
        
        <div className="progress-wrapper">
          <div className="progress-text">
            {stats.completed} of {stats.total} 
            {stats.completed >= stats.total && " (Completed)"}
          </div>
          <div className="progress-bar-compact">
            <div 
              className="progress-fill" 
              style={{ width: `${(stats.completed / stats.total) * 100}%` }}
            ></div>
          </div>
        </div>
        <button 
          className="view-results-button-compact"
          onClick={() => navigate(`/results/${experiment.id}`)}
        >
          Results
        </button>
      </div>
    );
  };

  if (loading && !comparison) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading comparison...</p>
      </div>
    );
  }

  // If no experimentId is provided, redirect to experiment selection
  if (!experimentId && !loading) {
    return (
      <div className="compare-container">
        <h1>Image Comparison</h1>
        
        {experiments.length > 0 ? (
          <>
            <p className="instruction-text">Please select an experiment to begin comparing images.</p>
            {renderExperimentSelection()}
          </>
        ) : (
          <div className="no-experiments">
            <h3>No Active Experiments</h3>
            <p>There are no active experiments available for comparison.</p>
            <button 
              className="create-experiment-button"
              onClick={() => navigate('/create-experiment')}
            >
              Create an Experiment
            </button>
          </div>
        )}
      </div>
    );
  }

  // If there's an experimentId, display the comparison interface
  if (experimentId) {
    return (
      <div className="content-container">
        <div className="comparison-container">
          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading experiment data...</p>
            </div>
          ) : experiments.length === 0 ? (
            <div className="no-experiments">
              <p>No experiments available for comparison</p>
            </div>
          ) : (
            <>
              <div className="comparison-controls">
                {renderExperimentSelection()}
                {experiment && renderExperimentProgress()}
              </div>
              
              <div className="images-container">
                {comparison ? (
                  <>
                    <div className="comparison-section">
                      <div className="comparison-images">
                        {renderImage(comparison.image1_url, 1)}
                        {renderImage(comparison.image2_url, 2)}
                      </div>
                      <div className="comparison-buttons">
                        <button 
                          className={`comparison-button ${submitting ? 'submitting' : ''}`}
                          onClick={() => handleVote('A')}
                          disabled={imageLoadingStates.image_1 || imageLoadingStates.image_2 || submitting}
                        >
                          {submitting ? 'Submitting...' : 'Choose Image A'}
                        </button>
                        <button 
                          className={`comparison-button ${submitting ? 'submitting' : ''}`}
                          onClick={() => handleVote('EquallyGood')}
                          disabled={imageLoadingStates.image_1 || imageLoadingStates.image_2 || submitting}
                        >
                          {submitting ? 'Submitting...' : 'Both Are Good'}
                        </button>
                        <button 
                          className={`comparison-button ${submitting ? 'submitting' : ''}`}
                          onClick={() => handleVote('EquallyBad')}
                          disabled={imageLoadingStates.image_1 || imageLoadingStates.image_2 || submitting}
                        >
                          {submitting ? 'Submitting...' : 'Both Are Bad'}
                        </button>
                        <button 
                          className={`comparison-button ${submitting ? 'submitting' : ''}`}
                          onClick={() => handleVote('B')}
                          disabled={imageLoadingStates.image_1 || imageLoadingStates.image_2 || submitting}
                        >
                          {submitting ? 'Submitting...' : 'Choose Image B'}
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="no-comparison">
                    <p>{error || "No more comparisons available for this experiment."}</p>
                    {experiment && stats.completed >= stats.total && (
                      <button 
                        className="view-results-button"
                        onClick={() => navigate(`/results/${experiment.id}`)}
                      >
                        View Results
                      </button>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="compare-container">
      <h1>Image Comparison</h1>
      
      {experiments.length > 0 ? (
        <>
          <p className="instruction-text">Please select an experiment to begin comparing images.</p>
          {renderExperimentSelection()}
        </>
      ) : (
        <div className="no-experiments">
          <h3>No Active Experiments</h3>
          <p>There are no active experiments available for comparison.</p>
          <button 
            className="create-experiment-button"
            onClick={() => navigate('/create-experiment')}
          >
            Create an Experiment
          </button>
        </div>
      )}
    </div>
  );
}

export default Compare; 